import React, { useState } from 'react';
import { ArrowRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const DataNews = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  const filters = ['All', 'Press Releases', 'Reports', 'Statistics', 'Announcements'];

  const newsItems = [
    {
      date: 'December 31, 2024',
      category: 'Announcements',
      title: 'Chief Justice Roberts Issues 2024 Year-End Report on the Federal Judiciary',
      excerpt:
        'Chief Justice John G. Roberts Jr. released his annual Year-End Report on the Federal Judiciary, reflecting on court operations, caseload trends, and emerging challenges.',
      featured: true,
    },
    {
      date: 'December 24, 2024',
      category: 'Press Releases',
      title: 'Statement Regarding Veto of the JUDGES Act',
      excerpt:
        'Judge Robert J. Conrad, Jr., Director of the Administrative Office, issued a statement responding to the recent presidential veto.',
      featured: false,
    },
    {
      date: 'December 19, 2024',
      category: 'Announcements',
      title: 'New Courthouse Learning Center Teaches Minnesota Students About Judiciary',
      excerpt:
        'A new civics education center in St. Paul is preparing students to be future civic leaders through hands-on learning about the federal court system.',
      featured: false,
    },
    {
      date: 'December 12, 2024',
      category: 'Announcements',
      title: 'Students See Their Future in the Judiciary as They Celebrate Bill of Rights Day',
      excerpt:
        'Federal courts across the country hosted special events marking the 233rd anniversary of the ratification of the Bill of Rights.',
      featured: false,
    },
    {
      date: 'November 30, 2024',
      category: 'Statistics',
      title: 'FY2024 Federal Court Management Statistics Released',
      excerpt:
        'The latest caseload statistics show filings across district, appellate, and bankruptcy courts for fiscal year 2024.',
      featured: false,
    },
    {
      date: 'November 18, 2024',
      category: 'Reports',
      title: '2024 Federal Judicial Caseload Statistics — 12-Month Period Ending March 31',
      excerpt:
        'Comprehensive data on the workload of the federal courts including civil, criminal, appellate, and bankruptcy filings.',
      featured: false,
    },
  ];

  const reports = [
    { title: 'Federal Judicial Caseload Statistics', period: '12-Month Period Ending March 31, 2024', type: 'Statistics' },
    { title: "Director's Annual Report 2023", period: 'Annual Report', type: 'Report' },
    { title: 'Bankruptcy Statistics', period: 'Quarter Ending September 30, 2024', type: 'Statistics' },
    { title: 'Civil Justice Reform Report', period: 'Annual', type: 'Report' },
  ];

  const filtered =
    activeFilter === 'All'
      ? newsItems
      : newsItems.filter((n) => n.category === activeFilter);

  const categoryColors = {
    'Press Releases': 'bg-blue-100 text-blue-700',
    Reports: 'bg-purple-100 text-purple-700',
    Statistics: 'bg-green-100 text-green-700',
    Announcements: 'bg-amber-100 text-amber-700',
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner */}
      <div
        className="relative py-20 px-8"
        style={{
          background: 'linear-gradient(135deg, #20476f 0%, #0d3559 50%, #1a5276 100%)',
        }}
      >
        <div className="max-w-[65rem] mx-auto relative z-10">
          <p className="text-blue-200 text-sm mb-4">Home &rsaquo; Data &amp; News</p>
          <h1
            className="text-5xl font-light text-white mb-6 leading-tight"
            style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
          >
            Data &amp; News
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl leading-relaxed mb-8">
            Stay informed with the latest news, press releases, statistical reports,
            and publications from the federal judiciary.
          </p>

          {/* Search bar */}
          <div className="relative max-w-xl">
            <input
              type="text"
              placeholder="Search news and data..."
              className="w-full pl-5 pr-14 py-4 rounded-full bg-white/10 border border-white/30 text-white placeholder-blue-200 focus:outline-none focus:bg-white/20 focus:border-white transition-all"
            />
            <button className="absolute right-4 top-1/2 -translate-y-1/2 text-blue-200 hover:text-white">
              <MagnifyingGlassIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[65rem] mx-auto px-8 py-16">
        <div className="flex flex-col md:flex-row gap-12">

          {/* Left: News Feed */}
          <div className="flex-1">
            {/* Filter Pills */}
            <div className="flex flex-wrap gap-2 mb-8">
              {filters.map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeFilter === f
                      ? 'bg-[#0057a3] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* Featured story */}
            {activeFilter === 'All' && filtered[0] && (
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-8 mb-6 hover:border-[#0057a3] transition-colors cursor-pointer">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${categoryColors[filtered[0].category]}`}>
                  {filtered[0].category}
                </span>
                <h2
                  className="text-2xl font-semibold text-gray-900 mb-3 hover:text-[#0057a3] transition-colors"
                  style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
                >
                  {filtered[0].title}
                </h2>
                <p className="text-gray-600 mb-4 leading-relaxed">{filtered[0].excerpt}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">{filtered[0].date}</span>
                  <a href="#" className="inline-flex items-center text-[#0057a3] text-sm font-medium hover:underline">
                    Read more <ArrowRightIcon className="w-3.5 h-3.5 ml-1.5" />
                  </a>
                </div>
              </div>
            )}

            {/* News list */}
            <div className="space-y-0 divide-y divide-gray-100">
              {(activeFilter === 'All' ? filtered.slice(1) : filtered).map((item) => (
                <div key={item.title} className="py-6 hover:bg-gray-50 -mx-4 px-4 rounded-xl transition-colors cursor-pointer">
                  <div className="flex flex-col md:flex-row md:items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${categoryColors[item.category]}`}>
                          {item.category}
                        </span>
                        <span className="text-xs text-gray-400">{item.date}</span>
                      </div>
                      <a
                        href="#"
                        className="text-[#0057a3] font-semibold text-lg hover:underline block mb-2"
                        style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
                      >
                        {item.title}
                      </a>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.excerpt}</p>
                    </div>
                    <ArrowRightIcon className="w-5 h-5 text-gray-300 flex-shrink-0 mt-1 hidden md:block" />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <button className="inline-flex items-center px-6 py-3 border-2 border-[#0057a3] text-[#0057a3] hover:bg-[#0057a3] hover:text-white font-medium rounded-full transition-colors">
                Load More Stories
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>

          {/* Right: Sidebar */}
          <div className="w-full md:w-72 flex-shrink-0 space-y-6">
            {/* Reports */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h3
                className="font-semibold text-gray-900 mb-5"
                style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
              >
                Statistical Reports
              </h3>
              <div className="space-y-4">
                {reports.map((r) => (
                  <div key={r.title} className="group cursor-pointer">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-[#0057a3] flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                        </svg>
                      </div>
                      <div>
                        <a href="#" className="text-sm font-medium text-[#0057a3] hover:underline group-hover:underline block leading-tight">
                          {r.title}
                        </a>
                        <span className="text-xs text-gray-400 mt-0.5 block">{r.period}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-gray-100">
                <a href="#" className="inline-flex items-center text-[#0057a3] text-sm font-medium hover:underline">
                  All statistical reports <ArrowRightIcon className="w-3.5 h-3.5 ml-1.5" />
                </a>
              </div>
            </div>

            {/* Subscribe */}
            <div className="bg-[#20476f] rounded-2xl p-6 text-white">
              <h3 className="font-semibold mb-2">Stay Informed</h3>
              <p className="text-sm text-blue-200 mb-4 leading-relaxed">
                Subscribe to receive judiciary news and press releases by email.
              </p>
              <input
                type="email"
                placeholder="Your email address"
                className="w-full px-4 py-2.5 rounded-full bg-white/10 border border-white/30 text-white placeholder-blue-300 text-sm focus:outline-none focus:bg-white/20 mb-3"
              />
              <button className="w-full py-2.5 bg-[#efd388] hover:bg-yellow-300 text-gray-900 text-sm font-semibold rounded-full transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataNews;